import type { Firestore } from 'firebase/firestore'
import type { LocalDexieDB } from './local-db'
import type { LocalStorageModuleDeps, Storage } from './types'
import { EmailInvoiceStorageModule } from '~/features/email-invoice-processing/storage'
import { AuthStorageModule } from '~common/google-auth/storage'
import { FaviconStorageModule } from '~common/favicon-fetcher/storage'
import { EntitlementsStorageModule } from '~/features/entitlements/storage'
import { EmailHtmlCacheLocalStorageModule } from '~/features/email-html-cache/storage'
import { BankTransactionStorageModule } from '~/features/transactions/storage'
import { MatchingAssociationsStorageModule } from '~/features/transactions/storage/matching-associations'
import { AutoFetchConfigStorageModule } from '~/features/auto-fetch-config/storage'
import { ShortcutsStorageModule } from './shortcuts'
import { DocumentIdCreator, StorageModuleDeps } from '~common/storage/types'
import { ProfilePicturesStorageModule } from './profilePictures'

export interface StorageDeps {
    initDb: () => Firestore
    initLocalDb: () => LocalDexieDB
    createDocumentId: DocumentIdCreator
    getCurrentUserId: () => string | null
}

export interface StorageModules {
    auth: AuthStorageModule
    emailInvoice: EmailInvoiceStorageModule
    favicon: FaviconStorageModule
    entitlements: EntitlementsStorageModule
    emailHtmlCache: EmailHtmlCacheLocalStorageModule
    bankTransaction: BankTransactionStorageModule
    matchingAssociations: MatchingAssociationsStorageModule
    autoFetchConfig: AutoFetchConfigStorageModule
    shortcuts: ShortcutsStorageModule
}

export function createStorage(deps: StorageDeps): Storage {
    const ensureAuthenticated = () => {
        const userId = deps.getCurrentUserId()
        if (!userId) {
            throw new UnauthenticatedStorageRequestError(
                'User must be authenticated to perform this operation',
            )
        }
        return userId
    }

    const db = deps.initDb()
    const storageModuleDeps: StorageModuleDeps = {
        db,
        createDocumentId: deps.createDocumentId,
        getCurrentUserId: deps.getCurrentUserId,
        ensureAuthenticated,
    }

    const localStorageModuleDeps: LocalStorageModuleDeps = {
        db: deps.initLocalDb(),
    }

    return {
        modules: {
            auth: new AuthStorageModule(storageModuleDeps),
            favicon: new FaviconStorageModule(storageModuleDeps),
            emailInvoice: new EmailInvoiceStorageModule(storageModuleDeps),
            entitlements: new EntitlementsStorageModule(storageModuleDeps),
            bankTransaction: new BankTransactionStorageModule(
                storageModuleDeps,
            ),
            matchingAssociations: new MatchingAssociationsStorageModule(
                storageModuleDeps,
            ),
            autoFetchConfig: new AutoFetchConfigStorageModule(
                storageModuleDeps,
            ),
            shortcuts: new ShortcutsStorageModule(storageModuleDeps),
            profilePictures: new ProfilePicturesStorageModule(
                storageModuleDeps,
            ),
        },
        localModules: {
            emailHtmlCache: new EmailHtmlCacheLocalStorageModule(
                localStorageModuleDeps,
            ),
        },
    }
}

export class UnauthenticatedStorageRequestError extends Error {
    constructor(message: string) {
        super(message)
        this.name = 'UnauthenticatedStorageRequestError'
        Object.setPrototypeOf(
            this,
            UnauthenticatedStorageRequestError.prototype,
        )
    }
}
